import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Drawer,
  Form,
  Input,
  Button,
  Space,
  Typography,
  message,
  Tag,
  Alert,
  Spin,
  Select,
  Switch,
  Divider,
  Tabs,
} from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  ThunderboltOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  FileAddOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { addAgent, checkAgentIdentity, draftAgent, getAgentById, searchAgents, type AgentIdentityClash } from '@/api/agents';
import { listModels, type ModelRow } from '@/api/models';
import type { AgentDefinition } from '@/api/types';
import { AgentPickerDrawer } from '@/components/AgentPickerDrawer';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

/** The name check waits for a typing pause, so a name costs one request, not one per letter. */
const IDENTITY_CHECK_PAUSE_MS = 300;

// ─────────────────────────────────────────────────────────────────────────────
// AgentEditorDrawer — smart "+ New agent" flow.
//
// Three modes, picked at the top of the drawer:
//   • Blank      — author from scratch
//   • Clone      — pick an existing agent → fields populate with " (copy)"
//                  suffix on the display name; you tweak and save
//   • AI co-pilot — describe in one sentence, model proposes name +
//                  description + audience + canonical slug
//
// Layout:
//   ┌────────────────────────────┬──────────────────────────────┐
//   │  FORM (60%)                │  SMART SIDEBAR (40%)         │
//   │  Display name              │  ✨ AI co-pilot              │
//   │  Canonical name            │  📋 Clone from existing      │
//   │  Audience toggles          │  ⚠ Lint                      │
//   │  Description               │                              │
//   │  Skill router toggle       │                              │
//   └────────────────────────────┴──────────────────────────────┘
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved?: (next: AgentDefinition) => void | Promise<void>;
}

interface FormValues {
  displayName: string;
  canonicalName?: string;
  description?: string;
  audience?: string;
  patientApp: boolean;
  merchantApp: boolean;
  enableSkillRouter: boolean;
  /** FK to ai.AIModel. Matches the column default so a blank form creates a working agent. */
  modelId: number;
}

/** Schema default for ai.AiAgent.ModelId. */
const DEFAULT_MODEL_ID = 27;

const blank = (): FormValues => ({
  displayName: '',
  canonicalName: '',
  description: '',
  audience: '',
  patientApp: false,
  merchantApp: false,
  enableSkillRouter: true,
  modelId: DEFAULT_MODEL_ID,
});

export const AgentEditorDrawer = observer(function AgentEditorDrawer({
  open,
  onClose,
  onSaved,
}: Props) {
  const { themeStore, merchantContextStore: ctx } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [form] = Form.useForm<FormValues>();
  const [values, setValues] = useState<FormValues>(blank());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // AI co-pilot state
  const [draftPrompt, setDraftPrompt] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [draftRationale, setDraftRationale] = useState<string | null>(null);
  const [similarNames, setSimilarNames] = useState<string[]>([]);

  // Clone-from-existing state — uses the rich AgentPickerDrawer (searchable
  // card grid with audience/status/router quick filters), same component the
  // app uses anywhere it asks the user to pick one of N agents.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState<number | null>(null);
  /** The row the picker handed back, for the trigger's label. */
  const [cloneSource, setCloneSource] = useState<AgentDefinition | null>(null);
  const [cloning, setCloning] = useState(false);

  // Fields the form does NOT surface but that we want carried over from
  // the source on a clone: the agent's actual prompt body (Instructions),
  // its Category, and the audience-routing flags. Stored separately so
  // the visible form stays focused on identity + audience while the
  // payload sent to /api/Agent/Add still inherits the full agent
  // shape that operators expect from a "Save As".
  const [cloneInherited, setCloneInherited] = useState<{
    instructions?: string;
    categoryId?: number;
  } | null>(null);

  // Model picker options — active models visible to this merchant. Loaded
  // once per open; failure degrades to a picker with only the default id.
  const [models, setModels] = useState<ModelRow[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fresh = blank();
    form.setFieldsValue(fresh);
    setValues(fresh);
    setError(null);
    setDraftPrompt('');
    setDraftRationale(null);
    setSimilarNames([]);
    setCloneSourceId(null);
    setCloneSource(null);
    setCloneInherited(null);
  }, [open, form]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setModelsLoading(true);
      try {
        const rows = await listModels({
          activeOnly: true,
          includeGlobal: true,
          merchantId: ctx.selectedMerchantId ?? undefined,
        });
        if (!cancelled) setModels(rows.filter((m) => m.active));
      } catch (err) {
        if (!cancelled) void message.warning('Could not load the model list: ' + (err as Error).message);
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, ctx.selectedMerchantId]);

  const modelOptions = useMemo(() => {
    const opts = models.map((m) => ({
      value: m.id,
      label: (
        <Space size={6}>
          <span>{m.title || m.name}</span>
          <Text type="secondary" style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
            {m.name}
          </Text>
          {!m.enableInChat && <Tag style={{ fontSize: 10, margin: 0 }}>chat off</Tag>}
          {!m.enableInProcess && <Tag style={{ fontSize: 10, margin: 0 }}>process off</Tag>}
        </Space>
      ),
      searchText: `${m.title ?? ''} ${m.name}`.toLowerCase(),
    }));
    // Keep the schema default selectable even if the list hasn't loaded
    // (or doesn't include it) so the form never holds an orphan value.
    if (!opts.some((o) => o.value === DEFAULT_MODEL_ID)) {
      opts.unshift({
        value: DEFAULT_MODEL_ID,
        label: <span>Platform default (model #{DEFAULT_MODEL_ID})</span>,
        searchText: 'platform default',
      });
    }
    return opts;
  }, [models]);

  // Auto-derive canonical name from display name when the user hasn't manually
  // edited it. Common pattern — saves a step for ~80% of cases.
  const [canonicalDirty, setCanonicalDirty] = useState(false);
  useEffect(() => {
    if (canonicalDirty) return;
    const slug = (values.displayName ?? '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    if (slug && slug !== values.canonicalName) {
      form.setFieldsValue({ canonicalName: slug });
      setValues((v) => ({ ...v, canonicalName: slug }));
    }
  }, [values.displayName, canonicalDirty, form, values.canonicalName]);

  // ── Identity collision check ─────────────────────────────────────────
  // THE SAVE'S OWN IDENTITY CHECK, asked as the names are typed (api/Agent/CheckIdentity → AIAgentConfigManager.CheckIdentity,
  // 2 Nov 2026): the same checks Add runs before it writes — canonical name, A2A name, and the runtime resolver's test
  // (a name that matches ANY identifier of another agent here makes routing fail closed) — against EVERY agent in the
  // practice, in the words Save would refuse with. It used to compare the names with the first 100 agents a store held,
  // so a clash with any other agent showed only when Save was refused. Save's own check stays the hard backstop.
  const [clashes, setClashes] = useState<AgentIdentityClash[]>([]);
  useEffect(() => {
    const merchantId = ctx.selectedMerchantId;
    const displayName = values.displayName?.trim();
    const canonicalName = values.canonicalName?.trim();
    if (!open || merchantId == null || (!displayName && !canonicalName)) {
      setClashes([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      checkAgentIdentity({ id: 0, merchantId, displayName, canonicalName })
        .then((found) => !cancelled && setClashes(found))
        .catch(() => !cancelled && setClashes([])); // unknown is not a clash; Save still checks
    }, IDENTITY_CHECK_PAUSE_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, ctx.selectedMerchantId, values.displayName, values.canonicalName]);

  const identity = useMemo(() => {
    const on = (field: string) => clashes.find((c) => c.field === field)?.message ?? null;
    return {
      displayName: on('DisplayName'),
      // The form has no A2A field: a clash there is about the identifier the canonical name stands for.
      canonicalName: on('CanonicalName') ?? on('A2AAgentName'),
      hasCollision: clashes.length > 0,
    };
  }, [clashes]);

  // ── Lint (sidebar) ───────────────────────────────────────────────────
  const lints = useMemo(() => {
    const out: string[] = [];
    const v = values;
    if (!v.displayName?.trim()) out.push('Display name is required.');
    else if (identity.displayName) out.push(`Display name — ${identity.displayName}`);
    if (identity.canonicalName) out.push(`Canonical name — ${identity.canonicalName}`);
    if (v.canonicalName && !/^[a-z0-9][a-z0-9-]*$/.test(v.canonicalName)) {
      out.push('Canonical name should be lowercase a-z, 0-9, hyphens only.');
    }
    if (!v.patientApp && !v.merchantApp) {
      out.push('Pick at least one audience (Patient or Care team) — or save as Internal-only.');
    }
    if (!v.description || v.description.length < 20) {
      out.push('Add a 1–2 sentence description so reviewers know the agent\'s scope.');
    }
    return out;
  }, [values, identity]);

  // ── AI co-pilot ──────────────────────────────────────────────────────
  const runDraft = async () => {
    if (draftPrompt.trim().length < 10) {
      void message.warning('Describe the agent you want — a sentence or two.');
      return;
    }
    if (ctx.selectedMerchantId == null) return;
    setDrafting(true);
    setError(null);
    try {
      const res = await draftAgent({
        merchantId: ctx.selectedMerchantId,
        userRequest: draftPrompt.trim(),
      });
      const next: FormValues = {
        ...values,
        displayName: res.displayName ?? values.displayName,
        canonicalName: res.canonicalName ?? values.canonicalName,
        description: res.description ?? values.description,
        audience: res.audience ?? values.audience,
        patientApp: res.patientApp ?? values.patientApp,
        merchantApp: res.merchantApp ?? values.merchantApp,
      };
      form.setFieldsValue(next);
      setValues(next);
      setDraftRationale(res.rationale ?? null);
      setSimilarNames(res.similarAgentNames ?? []);
      // The model just proposed a slug — accept it as user-intended.
      setCanonicalDirty(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDrafting(false);
    }
  };

  // ── Clone from existing ──────────────────────────────────────────────
  // Fault tolerance for clone:
  //   1. CanonicalName uniqueness — there's a UQ index on ai.AiAgent
  //      (UQ_AiAgent_CanonicalName) that rejects duplicates with a
  //      generic SQL error. To avoid surfacing that as an opaque 500
  //      after the user clicks Save, dedupe the slug client-side
  //      against the loaded registry. Fallback "<base>", "<base>-2",
  //      "<base>-3" up to a sane bound.
  //   2. Inherit Instructions + CategoryId — operators expect "Save
  //      As" to copy the agent's identity. Captured into cloneInherited
  //      so the visible form stays focused on identity tweaks while the
  //      save payload still carries the full agent shape.
  //   3. Defensive reads on the source object — older agent rows may
  //      be missing newer fields; coerce undefined → safe defaults
  //      rather than letting a typo throw.
  const runClone = async () => {
    if (cloneSourceId == null) {
      void message.warning('Pick an agent to copy from.');
      return;
    }
    setCloning(true);
    setError(null);
    try {
      const source = await getAgentById(cloneSourceId);
      const sourceAny = source as unknown as Record<string, unknown>;

      // Dedupe the slug against the practice. The base slug derives from
      // the new display name (which already has " (copy)" appended);
      // if THAT collides, append -2/-3/etc. until we find a free one.
      // Every slug it could collide with contains the base, so the server's
      // directory search for the base returns them all (the store this read
      // used to hold only the first 100 of the practice's agents).
      const baseSlug = `${source.displayName ?? 'Agent'} (copy)`
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      const merchantId = ctx.selectedMerchantId;
      const similar =
        merchantId == null || !baseSlug ? [] : (await searchAgents({ merchantId, search: baseSlug, pageSize: 200 })).data ?? [];
      const existingSlugs = new Set(
        similar
          .map((a) => a.canonicalName)
          .filter((s): s is string => !!s),
      );
      let uniqueSlug = baseSlug;
      let attempt = 2;
      while (existingSlugs.has(uniqueSlug) && attempt < 50) {
        uniqueSlug = `${baseSlug}-${attempt++}`;
      }

      const next: FormValues = {
        displayName: `${source.displayName ?? 'Agent'} (copy)`,
        canonicalName: uniqueSlug,
        description: source.description ?? '',
        audience: typeof sourceAny.audience === 'string' ? (sourceAny.audience as string) : '',
        patientApp: !!source.patientApp,
        merchantApp: !!source.merchantApp,
        enableSkillRouter: source.enableSkillRouter ?? true,
        // A copy runs on the same model as its source unless the operator
        // picks another one before saving.
        modelId: source.modelId || DEFAULT_MODEL_ID,
      };

      // Hidden inheritance — not surfaced in the visible form fields
      // but folded into the save payload so the cloned agent comes with
      // its prompt + category, matching operator "Save As" expectations.
      // Skills, knowledge links, peers, capabilities, foundry variants
      // are STILL not copied (each has its own provisioning lifecycle);
      // the toast below sets that expectation.
      setCloneInherited({
        instructions: source.instructions ?? undefined,
        categoryId: source.categoryId ?? undefined,
      });

      form.setFieldsValue(next);
      setValues(next);
      // Slug already deduped — mark canonical dirty so the
      // display-name auto-derive doesn't blow it away on a future
      // edit (the dedup already handled uniqueness).
      setCanonicalDirty(true);
      void message.info(
        `Cloned from "${source.displayName}". Prompt body and category were copied. Skills, knowledge, peers, and Foundry variants are NOT — link them after save.`,
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCloning(false);
    }
  };

  // ── Save ─────────────────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      const v = await form.validateFields();
      if (ctx.selectedMerchantId == null) {
        void message.error('Pick a merchant first.');
        return;
      }
      setSaving(true);
      setError(null);
      const created = await addAgent({
        displayName: v.displayName.trim(),
        canonicalName: v.canonicalName?.trim() || undefined,
        description: v.description?.trim() || undefined,
        audience: v.audience?.trim() || undefined,
        patientApp: v.patientApp,
        merchantApp: v.merchantApp,
        enableSkillRouter: v.enableSkillRouter,
        modelId: v.modelId ?? DEFAULT_MODEL_ID,
        merchantId: ctx.selectedMerchantId,
        // Inherited from a clone source (null when authoring from
        // scratch or via the AI co-pilot). The backend coalesces
        // missing categoryId to the General seed (Id=1) and treats
        // empty instructions as a blank-shell agent — both are safe
        // defaults.
        instructions: cloneInherited?.instructions,
        categoryId: cloneInherited?.categoryId,
      } as Partial<AgentDefinition>);
      void message.success(`Created ${created.displayName}.`);
      if (onSaved) await onSaved(created);
      onClose();
    } catch (err) {
      const msg = (err as { message?: string })?.message;
      if (msg) setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // Display name of the currently-selected clone source (for the trigger label): the row the picker handed back.
  const cloneSourceLabel =
    cloneSourceId == null
      ? null
      : (cloneSource?.id === cloneSourceId ? cloneSource.displayName : undefined) ?? `Agent #${cloneSourceId}`;

  return (
    <Drawer
      title={
        <Space size={8}>
          <FileAddOutlined style={{ color: colors.violet[500] }} />
          <span>New agent</span>
        </Space>
      }
      width="80%"
      open={open}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Space>
          <Button icon={<CloseOutlined />} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
            disabled={!values.displayName?.trim() || identity.hasCollision}
          >
            Create agent
          </Button>
        </Space>
      }
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
          gap: 16,
          height: '100%',
        }}
      >
        {/* ── Form ─────────────────────────────────────────────────── */}
        <div style={{ overflowY: 'auto', paddingRight: 8 }}>
          {error && (
            <Alert
              type="error"
              showIcon
              message="Couldn't save this agent"
              description={error}
              closable
              onClose={() => setError(null)}
              style={{ marginBottom: 12 }}
            />
          )}
          <Form
            form={form}
            layout="vertical"
            initialValues={values}
            onValuesChange={(_, all) => setValues(all)}
          >
            <Form.Item
              label="Display name"
              name="displayName"
              rules={[{ required: true, message: 'Display name is required' }]}
              tooltip="Human-readable name shown across the app."
              validateStatus={identity.displayName ? 'error' : undefined}
              help={identity.displayName || undefined}
            >
              <Input placeholder='e.g. "FOLFOX Patient Symptom Agent"' autoFocus />
            </Form.Item>

            <Form.Item
              label={
                <Space size={6}>
                  Canonical name
                  <Tag style={{ fontSize: 10 }}>auto-derived</Tag>
                </Space>
              }
              name="canonicalName"
              tooltip="Lowercase-hyphenated stable id. Used by scenarios + tests. Edit to override the auto-derive."
              validateStatus={identity.canonicalName ? 'error' : undefined}
              help={identity.canonicalName || undefined}
            >
              <Input
                placeholder="folfox-patient"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
                onChange={() => setCanonicalDirty(true)}
              />
            </Form.Item>

            <Form.Item label="Audience">
              <div style={{ display: 'flex', gap: 24 }}>
                <Form.Item name="patientApp" valuePropName="checked" noStyle>
                  <ToggleRow label="Patient" />
                </Form.Item>
                <Form.Item name="merchantApp" valuePropName="checked" noStyle>
                  <ToggleRow label="Care team" />
                </Form.Item>
              </div>
            </Form.Item>

            <Form.Item
              label="Audience label"
              name="audience"
              tooltip="Free-text label shown next to the agent. Examples: Patient, Care team, Provider, Internal."
            >
              <Select
                allowClear
                placeholder="Pick or type a label"
                options={[
                  { value: 'Patient', label: 'Patient' },
                  { value: 'Care team', label: 'Care team' },
                  { value: 'Provider', label: 'Provider' },
                  { value: 'Internal', label: 'Internal' },
                ]}
              />
            </Form.Item>

            <Form.Item
              label="Description"
              name="description"
              tooltip="One or two sentences explaining the agent's purpose and scope. Shown on the hero."
            >
              <TextArea rows={3} placeholder="What this agent does and who it talks to." />
            </Form.Item>

            <Form.Item
              label="Model"
              name="modelId"
              rules={[{ required: true, message: 'Pick a model' }]}
              help="Which model this agent runs on. Can be changed later from the agent's Model selection card."
            >
              <Select
                showSearch
                loading={modelsLoading}
                options={modelOptions}
                optionFilterProp="searchText"
                placeholder="Pick a model"
              />
            </Form.Item>

            <Divider style={{ margin: '12px 0' }} />

            <Space size={32} style={{ width: '100%' }}>
              <Form.Item label="Skill router" name="enableSkillRouter" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Switch checkedChildren="On" unCheckedChildren="Off" />
              </Form.Item>
              {/* A new agent starts as Draft (2 Nov 2026). "Active on save" was here and did nothing: the server never
                  read it. An agent becomes Active from its own page, once its evaluations satisfy someone. */}
              <Text type="secondary" style={{ fontSize: 12, maxWidth: 320, display: 'block' }}>
                Starts as <b>Draft</b>. Make it Active from its page once its evaluations are good.
              </Text>
            </Space>
          </Form>
        </div>

        {/* ── Smart sidebar ───────────────────────────────────────── */}
        <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Tabs
            defaultActiveKey="ai"
            size="small"
            items={[
              {
                key: 'ai',
                label: <Space size={4}><ThunderboltOutlined /> AI co-pilot</Space>,
                children: (
                  <SidebarCard isDark={isDark}>
                    <Paragraph type="secondary" style={{ fontSize: 12.5, marginBottom: 8 }}>
                      Describe the agent in plain English. The model proposes a name, canonical
                      slug, audience, and description that fit the existing roster.
                    </Paragraph>
                    <TextArea
                      rows={4}
                      placeholder='e.g. "patient-facing assistant for a colorectal patient on FOLFOX — focus on chemotherapy side effects, escalate emergencies"'
                      value={draftPrompt}
                      onChange={(e) => setDraftPrompt(e.target.value)}
                      style={{ fontSize: 13 }}
                    />
                    <Button
                      type="primary"
                      block
                      icon={<ThunderboltOutlined />}
                      loading={drafting}
                      onClick={runDraft}
                      disabled={draftPrompt.trim().length < 10}
                      style={{ marginTop: 8 }}
                    >
                      Draft with AI
                    </Button>
                    {draftRationale && (
                      <div
                        style={{
                          marginTop: 10,
                          padding: '8px 10px',
                          background: isDark ? colors.slate[800] : colors.slate[50],
                          border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                          borderRadius: 6,
                          fontSize: 12,
                          lineHeight: 1.55,
                          color: isDark ? colors.slate[300] : colors.slate[700],
                        }}
                      >
                        <Text strong style={{ fontSize: 11, textTransform: 'uppercase' }}>
                          Rationale
                        </Text>
                        <div style={{ marginTop: 4 }}>{draftRationale}</div>
                      </div>
                    )}
                    {similarNames.length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <Text type="warning" style={{ fontSize: 12 }}>
                          ⚠ Similar agents already exist:
                        </Text>
                        <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {similarNames.map((n) => (
                            <Tag key={n} color="orange">{n}</Tag>
                          ))}
                        </div>
                      </div>
                    )}
                  </SidebarCard>
                ),
              },
              {
                key: 'clone',
                label: <Space size={4}><CopyOutlined /> Clone</Space>,
                children: (
                  <SidebarCard isDark={isDark}>
                    <Paragraph type="secondary" style={{ fontSize: 12.5, marginBottom: 8 }}>
                      Pick an existing agent to copy. Display name, audience, description, and
                      router setting are duplicated. <strong>Skills, knowledge bases, and peer
                      links are not copied</strong> — link them after save.
                    </Paragraph>
                    <Button
                      block
                      onClick={() => setPickerOpen(true)}
                      style={{
                        height: 'auto',
                        padding: '10px 12px',
                        textAlign: 'left',
                        whiteSpace: 'normal',
                      }}
                    >
                      {cloneSourceLabel ? (
                        <Space size={6} style={{ width: '100%', justifyContent: 'space-between' }}>
                          <Text strong style={{ fontSize: 13 }}>{cloneSourceLabel}</Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>change…</Text>
                        </Space>
                      ) : (
                        <Space size={6}>
                          <CopyOutlined />
                          <span>Browse agents…</span>
                        </Space>
                      )}
                    </Button>
                    <Button
                      block
                      type="primary"
                      icon={<CopyOutlined />}
                      loading={cloning}
                      onClick={runClone}
                      disabled={cloneSourceId == null}
                      style={{ marginTop: 8 }}
                    >
                      Copy fields into form
                    </Button>
                  </SidebarCard>
                ),
              },
            ]}
          />

          {/* Lint */}
          <SidebarCard
            isDark={isDark}
            title={
              <Space size={6}>
                <span>Lint</span>
                <Tag color={lints.length === 0 ? 'green' : 'orange'}>{lints.length}</Tag>
              </Space>
            }
          >
            {lints.length === 0 ? (
              <Space size={6}>
                <CheckCircleOutlined style={{ color: colors.green[500] }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Looks good — ready to create.
                </Text>
              </Space>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: isDark ? colors.amber[500] : colors.amber[700] }}>
                {lints.map((l, i) => (
                  <li key={i} style={{ marginBottom: 4, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <WarningOutlined style={{ marginTop: 3 }} />
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
            )}
          </SidebarCard>

          {/* Tip */}
          {drafting || cloning ? (
            <div style={{ textAlign: 'center', padding: 8 }}>
              <Spin size="small" />
            </div>
          ) : (
            <Text type="secondary" style={{ fontSize: 11.5, paddingLeft: 4 }}>
              Tip: after creating, the agent opens automatically — link skills, knowledge, and peers from there.
            </Text>
          )}
        </div>
      </div>

      {/* Rich agent picker — same component used elsewhere in the app for
          one-of-N agent selection (peers, etc.). Lives nested inside the
          editor drawer so closing the picker doesn't close the parent. */}
      <AgentPickerDrawer
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selectedId={cloneSourceId}
        onSelect={(id, agent) => {
          setCloneSourceId(id);
          setCloneSource(agent ?? null);
          setPickerOpen(false);
        }}
        title="Pick an agent to copy from"
        allowClear={false}
      />
    </Drawer>
  );
});

// ── Sub-components ─────────────────────────────────────────────────────────

function SidebarCard({
  title,
  children,
  isDark,
}: {
  title?: React.ReactNode;
  children: React.ReactNode;
  isDark: boolean;
}) {
  return (
    <div
      style={{
        background: isDark ? colors.slate[900] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 8,
        padding: 12,
      }}
    >
      {title && <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 600 }}>{title}</div>}
      {children}
    </div>
  );
}

function ToggleRow({ label, checked, onChange }: { label: string; checked?: boolean; onChange?: (v: boolean) => void }) {
  return (
    <Space size={8}>
      <Switch checked={checked} onChange={onChange} size="small" />
      <Text>{label}</Text>
    </Space>
  );
}
